import glamorous from 'glamorous';
import React from 'react';
import { ColumnsUtils, createPureComponent } from 'module/Common';
import { Content, ContentItem, ContentItemType } from 'module/Model/Content';
import { Embed } from './items/Embed';
import { ImagesLinks } from './items/ImagesLinks';
import { MultipleImages } from './items/MultipleImages';
import { RichTextThreeCols } from './items/RichTextThreeCols';
import { RichTextTwoCols } from './items/RichTextTwoCols';
import { RitchText } from './items/RichText';
import { SingleImage } from './items/SingleImage';
import { Sizes } from 'module/View/Common';
import { Space } from './items/Space';
import { SubTitle } from './items/SubTitle';
import posed from 'react-pose';
import { AnnimStateProvider } from './AnnimStateProvider';

type ItemProps = {
  spaceBetween: number;
  isFirst: boolean;
  isLast: boolean;
};

const Item = glamorous.div<ItemProps>(p => ({
  marginTop: p.isFirst ? 0 : p.spaceBetween,
  marginBottom: p.isLast ? p.spaceBetween : 0,
  pointerEvents: 'none',
}));

type WrapperProps = {
  width: number;
  zIndex: number;
};

const Wrapper = glamorous.div<WrapperProps>(p => ({
  width: p.width,
  overflow: 'hidden',
  zIndex: p.zIndex,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  pointerEvents: 'none',
}));

type HiderProps = { coverColor: string; spaceBetween: number };

const Hider = glamorous.div<HiderProps>(p => ({
  position: 'absolute',
  left: 0,
  top: 0,
  right: 0,
  bottom: p.spaceBetween,
  background: p.coverColor,
  zIndex: 20,
  transform: 'translateY(-100%) translateZ(0px)',
  transitionDuration: '0.3s',
}));

const FadePose = posed.div({
  init: {
    opacity: 0,
  },
  mounted: {
    opacity: 1,
  },
});

type Props = {
  items: Content;
  cols: ColumnsUtils.ColumnsResult;
  availableWidth: number;
  innerRef?: (elem: HTMLElement | null) => void;
  zIndex?: number;
  isCover?: boolean;
  coverviewColor: string | null;
};

export const ContentItems = createPureComponent<Props>(
  'ContentItems',
  ({ items, cols, availableWidth, isCover = false, innerRef, zIndex = 100, coverviewColor }) => {
    const theWidth = isCover ? cols.size(Sizes.allColumnsMinusOne(cols.nbOfColumns)) : availableWidth;

    return (
      <AnnimStateProvider waitForImages={isCover}>
        {annimParams => {
          const itemsStyle = !isCover
            ? { position: 'relative' }
            : { position: 'relative', opacity: annimParams.pose === 'normal' ? 1 : 0 };
          const translate = (() => {
            switch (annimParams.pose) {
              case 'init':
                return '-100%';
              case 'mounted':
                return '0%';
              case 'normal':
                return '110%';
            }
            return '110%';
          })();
          return (
            <Wrapper width={theWidth} zIndex={zIndex} innerRef={innerRef}>
              {isCover &&
                coverviewColor && (
                  <Hider
                    style={{ transform: `translateY(${translate}) translateZ(0px)` }}
                    spaceBetween={cols.spaceBetween}
                    coverColor={coverviewColor}
                  />
                )}
              <FadePose style={itemsStyle as any} pose={!isCover && annimParams.pose === 'init' ? 'init' : 'mounted'}>
                {items.map((contentItem, index) => (
                  <Item
                    spaceBetween={cols.spaceBetween}
                    key={index}
                    isFirst={index === 0}
                    isLast={index === items.length - 1}
                  >
                    {(() => {
                      const commonProps = {
                        item: contentItem as any,
                        cols,
                        itemIndex: index,
                        isCover,
                        waitForImage: annimParams.waitForImage,
                        imageReady: annimParams.imageReady,
                      };
                      switch (ContentItem.getType(contentItem)) {
                        case ContentItemType.RichText:
                          return <RitchText {...commonProps} />;
                        case ContentItemType.SingleImage:
                          return <SingleImage {...commonProps} />;
                        case ContentItemType.MultipleImages:
                          return <MultipleImages {...commonProps} />;
                        case ContentItemType.SubTitle:
                          return <SubTitle {...commonProps} />;
                        case ContentItemType.RichTextTwoCols:
                          return <RichTextTwoCols {...commonProps} availableWidth={availableWidth} />;
                        case ContentItemType.RichTextThreeCols:
                          return <RichTextThreeCols {...commonProps} availableWidth={availableWidth} />;
                        case ContentItemType.Embed:
                          return <Embed {...commonProps} />;
                        case ContentItemType.ImagesLinks:
                          return <ImagesLinks {...commonProps} availableWidth={availableWidth} />;
                        case ContentItemType.Space:
                          return <Space item={contentItem as any} />;
                        default:
                          return <p>Unknown content item type : {ContentItem.getType(contentItem)}</p>;
                      }
                    })()}
                  </Item>
                ))}
              </FadePose>
            </Wrapper>
          );
        }}
      </AnnimStateProvider>
    );
  }
);



// WEBPACK FOOTER //
// ./src/module/View/Content/component/ContentItems/index.tsx